import { Drawer, Button, notification, Card, Typography, Skeleton, Avatar, InputNumber, Input, Modal, Form } from "antd";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";

import { fetchCreatorEarnings, fetchCreatorPayments, saveCreatorPayments } from '../../actions/creator';

import CreatorEarnings from "../../CreatorPages/CreatorEarnings";
import CreatorPayments from "../../CreatorPages/CreatorPayments";


const { Text } = Typography;
const { confirm } = Modal;
const { Meta } = Card;

const ManageEarningDrawer = ({
    creator,
    onDrawerClose
}) => {

    const dispatch = useDispatch();
    const [creatorEarnings, setCreatorEarnings] = useState(null);
    const [creatorEarningsStatus, setCreatorEarningsStatus] = useState("LOADING");
    const [creatorPayments, setCreatorPayments] = useState([]);
    const [creatorPaymentsStatus, setCreatorPaymentsStatus] = useState("LOADING");

    const [refreshAfterClose, setRefreshAfterClose] = useState(false);
    const [form] = Form.useForm();
    const [isFormValid, setIsFormValid] = useState(false);
    const formItemStyle = (width) => ({
        width,
        marginRight: '8px',
    });
    const layout = {
        layout: 'inline',
    };
    const onFinish = (values) => {
        confirm({
            title: "Are you sure you want to create this payment?",
            content: 'You are about to create $' + values.amount + ' payment for ' + creator.firstName + ' ' + creator.lastName,
            okText: 'Yes',
            okType: 'danger',
            cancelText: 'No',
            onOk() {
                dispatch(saveCreatorPayments(creator.id, values)).then(() => {
                    reloadData();
                    setRefreshAfterClose(true);
                    notification.success({
                        message: `Payment creation successfull!`
                    });
                }).catch((error) => {
                    notification.error({
                        message: `Payment creation failed!`,
                        description: error.message || 'An error occurred while creating the payment.'
                    });
                });
            },
            onCancel() {
                console.log('Cancelled');
            },
        });
    };

    // Validate on every form update
    const handleFormChange = () => {
        const hasErrors = form
            .getFieldsError()
            .some(({ errors }) => errors.length > 0);
        setIsFormValid(!hasErrors);
    };

    const onClose = () => {
        onDrawerClose(refreshAfterClose)
    };

    const CreatorInfo = () => {
        return (
            <Card className="creator-author-page-card">

                <Skeleton loading={false} avatar>
                    <Meta
                        avatar={<Avatar size={56} src={creator.profilePicture} />}
                        title={`${creator.firstName} ${creator.lastName}`}
                        description={`${creator.username} [${creator.id}]`}
                    />
                </Skeleton>
            </Card>
        )
    }

    const CreatorEarningSection = () => {
        return (
            <div style={{ marginTop: "0.5rem" }}>
                <CreatorEarnings
                    creatorEarnings={creatorEarnings}
                    creatorEarningsStatus={creatorEarningsStatus}
                />
            </div>
        );
    }

    const CreatorPaymentFormSection = () => {
        return (
            <div style={{ marginTop: "0.5rem" }}>
                <Card className="creator-author-page-card">
                    <Text>Make a payment</Text>
                    <br />
                    <Form form={form} {...layout} onFinish={onFinish} onFieldsChange={handleFormChange}>
                        <Form.Item
                            name="amount"
                            rules={[{ required: true, message: 'Please enter an amount' }]}
                            style={formItemStyle('30%')}
                        >
                            <InputNumber
                                size="large"
                                prefix="US $"
                                min={1}
                                max={creator ? (creator.totalEarnings - creator.paidEarnings) : 0}
                                step={0.01}
                                precision={2}
                                style={{ width: '100%' }}
                                formatter={(value) =>
                                    `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                                }
                                parser={(value) => value.replace(/[^0-9.]/g, '')}
                                placeholder="Amount"
                            />
                        </Form.Item>

                        <Form.Item
                            name="reference"
                            style={formItemStyle('40%')}
                        >
                            <Input size="large" placeholder="Payment Reference" />
                        </Form.Item>

                        <Form.Item style={formItemStyle('20%')}>
                            <Button
                                disabled={!isFormValid}
                                size="large" type="primary" htmlType="submit" block>
                                Submit
                            </Button>
                        </Form.Item>
                    </Form>
                </Card>

            </div>
        );
    }

    const CreatorPaymentsSection = () => {
        return (
            <div style={{ marginTop: "0.5rem" }}>
                <CreatorPayments
                    creatorsPayments={creatorPayments}
                    creatorPaymentsStatus={creatorPaymentsStatus}
                />
            </div>
        );
    }

    const loadCretorEarnings = () => {
        setCreatorEarningsStatus("LOADING");
        dispatch(fetchCreatorEarnings(creator.id)).then((data) => {
            if (data) {
                setCreatorEarnings(data);
                setCreatorEarningsStatus("LOADED");

                // Reset form values
                form.setFieldsValue({
                    amount: data.pendingEarnings,
                    reference: ''
                });
            } else {
                setCreatorEarningsStatus("FAILED");
            }
        });
    }

    const loadCretorPayments = () => {
        setCreatorPaymentsStatus("LOADING");
        dispatch(fetchCreatorPayments(creator.id)).then((data) => {
            if (data) {
                setCreatorPayments(data);
                setCreatorPaymentsStatus("LOADED");
            } else {
                setCreatorPaymentsStatus("FAILED");
            }
        });
    }

    const reloadData = () => {
        loadCretorEarnings();
        loadCretorPayments();
    }

    useEffect(() => {
        if (creator) {
            reloadData();
            handleFormChange();
        }
    }, [creator]);


    return (
        <Drawer
            title="Manage Earnings"
            placement="right"
            closable={false}
            forceRender={true}
            onClose={() => onClose()}
            visible={true}
            width={"37.5vw"}
            mask={true}
            maskClosable={false}
            footer={
                <div
                    style={{
                        textAlign: "right"
                    }}>
                    <Button style={{ marginRight: 8 }} onClick={onClose}>
                        Close
                    </Button>
                </div>
            }>
            <div className="manage-earning-drawer-content">

                <CreatorInfo />
                <CreatorEarningSection />
                {creator && (creator.totalEarnings - creator.paidEarnings) > 0 && (
                    <CreatorPaymentFormSection />
                )}
                <CreatorPaymentsSection />
            </div>
        </Drawer>
    );
};

export default ManageEarningDrawer;
